import React, { useState } from 'react'
import { useParams } from 'react-router-dom'
import AppLayout from '../components/AppLayout'
import { useApp } from '../context/AppContext'

const defaultCategories = [
  'Bestik',
  'Service',
  'Glas',
  'Møbler',
  'Tekstil',
]

const defaultItems = {
  'Bestik': [
    { name: 'Gafler' },
    { name: 'Knive' },
    { name: 'Spiseske' },
    { name: 'Dessertske' },
  ],
  'Service': [
    { name: 'Middagstallerkener' },
    { name: 'Frokosttallerkener' },
    { name: 'Kagstallerkener' },
    { name: 'Dybe skåle' },
    { name: 'Store tallerkener' },
  ],
  'Glas': [
    { name: 'Champagneglas' },
    { name: 'Vandglas' },
    { name: 'Rødvinsglas' },
    { name: 'Hvidvinsglas' },
  ],
  'Møbler': [
    { name: 'Borde' },
    { name: 'Stole' },
  ],
  'Tekstil': [
    { name: 'Duge' },
    { name: 'Servietter (stof)' },
    { name: 'Servietter (papir)' },
  ],
}

export default function Inventar() {
  const { eventId } = useParams()
  const { events, inventar, addInventarItem, updateInventarItem, deleteInventarItem, canDelete } = useApp()
  const [openCats, setOpenCats] = useState(new Set(['Bestik']))
  const [addForm, setAddForm] = useState(null)
  const [addCategoryModal, setAddCategoryModal] = useState(false)
  const [newCategoryName, setNewCategoryName] = useState('')
  const [editingItem, setEditingItem] = useState(null)
  const [editForm, setEditForm] = useState(null)
  const [showStatModal, setShowStatModal] = useState(null)
  const [editingValues, setEditingValues] = useState({})
  const [loanForm, setLoanForm] = useState(null)
  const [openLaantCat, setOpenLaantCat] = useState(false)
  const [loans, setLoans] = useState({})
  const [loanPerson, setLoanPerson] = useState('')
  const [loanItem, setLoanItem] = useState(null)
  const [emptyCategories, setEmptyCategories] = useState([])

  const event = events && events.find(e => e.id === eventId)
  const items = inventar[eventId] || []


  const groupItems = () => {
    const grouped = {}
    defaultCategories.forEach(cat => {
      const defaultNames = new Set(defaultItems[cat].map(d => d.name))
      const allCatItems = defaultItems[cat].map(defaultItem => {
        const existingItem = items.find(i => i.categoryId === cat && i.name === defaultItem.name)
        return {
          id: existingItem?.id,
          categoryId: cat,
          name: defaultItem.name,
          totalOwned: existingItem?.totalOwned || 0,
          neededForEvent: existingItem?.neededForEvent || 0,
        }
      })

      // Also add custom items in this category that aren't in defaultItems
      const customItems = items.filter(i => i.categoryId === cat && !defaultNames.has(i.name)).map(item => ({
        id: item.id,
        categoryId: cat,
        name: item.name,
        totalOwned: item.totalOwned || 0,
        neededForEvent: item.neededForEvent || 0,
      }))

      grouped[cat] = [...allCatItems, ...customItems]
    })

    // Add custom categories
    const customCats = [...new Set(items.filter(i => !defaultCategories.includes(i.categoryId)).map(i => i.categoryId))]
    customCats.forEach(cat => {
      const catItems = items.filter(i => i.categoryId === cat)
      grouped[cat] = catItems.map(item => ({
        id: item.id,
        categoryId: cat,
        name: item.name,
        totalOwned: item.totalOwned || 0,
        neededForEvent: item.neededForEvent || 0,
      }))
    })

    // Add empty categories that were just created
    emptyCategories.forEach(cat => {
      if (!grouped[cat]) {
        grouped[cat] = []
      }
    })

    return grouped
  }

  const addLoan = (itemName, personName, amount) => {
    if (!itemName || !personName || !amount) return
    const loanId = Date.now().toString()
    setLoans(prev => ({
      ...prev,
      [loanId]: {
        id: loanId,
        item: itemName,
        person: personName,
        amount: parseInt(amount)
      }
    }))
    setLoanForm(null)
  }

  const removeLoan = (loanId) => {
    setLoans(prev => {
      const newLoans = { ...prev }
      delete newLoans[loanId]
      return newLoans
    })
  }

  const calculateStats = () => {
    const grouped = groupItems()
    const allItems = Object.values(grouped).flat()
    const harNok = allItems.filter(i => i.id && i.totalOwned >= i.neededForEvent && i.neededForEvent > 0)
    const mangler = allItems.filter(i => i.id && i.totalOwned < i.neededForEvent && i.neededForEvent > 0 && i.totalOwned > 0)
    const skalLåne = allItems.filter(i => i.id && i.totalOwned === 0 && i.neededForEvent > 0)
    return { harNok, mangler, skalLåne }
  }

  const handleAddItem = async (categoryId, itemName) => {
    if (!itemName || !itemName.trim()) {
      alert('Skriv et navn på varen først')
      return
    }
    if (!categoryId) {
      alert('Vælg en kategori først')
      return
    }
    try {
      console.log('Adding item:', { categoryId, itemName })
      await addInventarItem(eventId, {
        categoryId,
        name: itemName.trim(),
        totalOwned: 0,
        neededForEvent: 0,
      })
      console.log('Item added successfully')
      // Remove from empty categories if it was just created
      setEmptyCategories(prev => prev.filter(cat => cat !== categoryId))
      // Keep category open and clear input
      setAddForm({ categoryId, name: '' })
    } catch (error) {
      console.error('Error adding item:', error)
      alert('Fejl ved tilføjelse: ' + error.message)
    }
  }

  const handleUpdateItem = async (item, field, value) => {
    try {
      // If item doesn't exist yet (no ID), create it first
      if (!item.id) {
        await addInventarItem(eventId, {
          categoryId: item.categoryId,
          name: item.name,
          totalOwned: field === 'totalOwned' ? parseInt(value) || 0 : 0,
          neededForEvent: field === 'neededForEvent' ? parseInt(value) || 0 : 0,
        })
      } else {
        await updateInventarItem(eventId, item.id, { [field]: parseInt(value) || 0 })
      }
    } catch (error) {
      console.error('Error updating item:', error)
    }
  }

  const handleDeleteItem = async (itemId) => {
    if (!itemId) return
    try {
      await deleteInventarItem(eventId, itemId)
    } catch (error) {
      console.error('Error deleting item:', error)
    }
  }

  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) return
    const catName = newCategoryName.trim()
    setAddCategoryModal(false)
    setNewCategoryName('')
    // Add to empty categories and open it
    setEmptyCategories(prev => [...prev, catName])
    setOpenCats(prev => new Set([...prev, catName]))
    setAddForm({ categoryId: catName, name: '' })
  }

  const grouped = groupItems()
  const allCategories = Object.keys(grouped)
  const stats = calculateStats()

  return (
    <AppLayout>
      <div className="page-header">
        <div className="page-header-row1">
          <div className="page-header-left">
            <h1 className="page-title">{event?.name}</h1>
            <span className="page-subtitle">Inventar</span>
          </div>
        </div>
      </div>

      <div className="page-content">
        <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(4,1fr)' }}>
          <div
            className="stat-card"
            style={{ background: '#e8fdf5', cursor: stats.harNok.length > 0 ? 'pointer' : 'default' }}
            onClick={() => stats.harNok.length > 0 && setShowStatModal('harNok')}
          >
            <div className="stat-label" style={{ color: '#007a58' }}>har nok</div>
            <div className="stat-value">{stats.harNok.length}</div>
            {stats.harNok.length > 0 && <div style={{ fontSize: 10, color: '#007a58', marginTop: 8 }}>klik for detaljer</div>}
          </div>
          <div
            className="stat-card"
            style={{ background: '#fff3e0', cursor: stats.mangler.length > 0 ? 'pointer' : 'default' }}
            onClick={() => stats.mangler.length > 0 && setShowStatModal('mangler')}
          >
            <div className="stat-label" style={{ color: '#9a5800' }}>mangler</div>
            <div className="stat-value">{stats.mangler.length}</div>
            {stats.mangler.length > 0 && <div style={{ fontSize: 10, color: '#9a5800', marginTop: 8 }}>klik for detaljer</div>}
          </div>
          <div
            className="stat-card"
            style={{ background: '#ffe4e4', cursor: stats.skalLåne.length > 0 ? 'pointer' : 'default' }}
            onClick={() => stats.skalLåne.length > 0 && setShowStatModal('skalLåne')}
          >
            <div className="stat-label" style={{ color: '#cc2020' }}>skal låne</div>
            <div className="stat-value">{stats.skalLåne.length}</div>
            {stats.skalLåne.length > 0 && <div style={{ fontSize: 10, color: '#cc2020', marginTop: 8 }}>klik for detaljer</div>}
          </div>
          <div
            className="stat-card"
            style={{ background: '#f0eeff', cursor: Object.keys(loans).length > 0 ? 'pointer' : 'default' }}
            onClick={() => Object.keys(loans).length > 0 && setOpenLaantCat(!openLaantCat)}
          >
            <div className="stat-label" style={{ color: '#6c63ff' }}>lånt</div>
            <div className="stat-value">{Object.keys(loans).length}</div>
            {Object.keys(loans).length > 0 && <div style={{ fontSize: 10, color: '#6c63ff', marginTop: 8 }}>klik for detaljer</div>}
          </div>
        </div>

        {openLaantCat && Object.keys(loans).length > 0 && (
          <div style={{ marginTop: 16, background: '#f0eeff', borderRadius: 12, padding: 16 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#6c63ff', marginBottom: 12 }}>Lånt:</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {Object.values(loans).map(loan => (
                <div key={loan.id} style={{ background: '#fff', borderRadius: 8, padding: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#1a1a2e' }}>{loan.amount} {loan.item}</div>
                    <div style={{ fontSize: 11, color: '#DF83A9', marginTop: 2 }}>af {loan.person}</div>
                  </div>
                  <button
                    onClick={() => removeLoan(loan.id)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#DF83A9',
                      cursor: 'pointer',
                      fontSize: 14,
                      padding: '4px 8px',
                    }}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div style={{ marginTop: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <h2 style={{ fontSize: 18, fontWeight: 700, color: '#1a1a2e' }}>Inventar</h2>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={() => setOpenCats(new Set(allCategories))}
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: '#6c63ff',
                  background: 'none',
                  border: '1px solid #e0d5ff',
                  borderRadius: 6,
                  padding: '6px 12px',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                åbn alle
              </button>
              <button
                onClick={() => setOpenCats(new Set())}
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: '#DF83A9',
                  background: 'none',
                  border: '1px solid #e0e0e0',
                  borderRadius: 6,
                  padding: '6px 12px',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                luk alle
              </button>
            </div>
          </div>

          <div className="card">
            {allCategories.map(categoryId => {
              const categoryItems = grouped[categoryId]
              const isOpen = openCats.has(categoryId)
              return (
                <div key={categoryId}>
                  <div
                    className="budget-cat-header"
                    style={{ background: '#f7f5ff', padding: '12px 14px', cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => {
                      const newOpen = new Set(openCats)
                      if (newOpen.has(categoryId)) {
                        newOpen.delete(categoryId)
                      } else {
                        newOpen.add(categoryId)
                      }
                      setOpenCats(newOpen)
                    }}
                  >
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#1a1a2e' }}>{categoryId}</span>
                    <svg
                      className={`budget-chevron ${isOpen ? 'open' : ''}`}
                      style={{ color: '#6c63ff', marginLeft: 'auto' }}
                      viewBox="0 0 8 8"
                      fill="none"
                      width="16"
                      height="16"
                    >
                      <path d="M2 3l2 2 2-2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
                    </svg>
                  </div>

                  {isOpen && (
                    <div style={{ padding: '12px 0' }}>
                      {categoryItems.map((item, idx) => (
                          <div
                            key={idx}
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 8,
                              padding: '10px 14px',
                              borderBottom: '1px solid #f0eeff',
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              <span style={{ fontSize: 12, fontWeight: 500, color: '#1a1a2e' }}>
                                {item.name}
                              </span>
                              {item.id && canDelete(eventId) && (
                                <button
                                  onClick={() => handleDeleteItem(item.id)}
                                  style={{
                                    background: 'none',
                                    border: 'none',
                                    color: '#DF83A9',
                                    cursor: 'pointer',
                                    fontSize: 12,
                                    padding: '2px 6px',
                                  }}
                                >
                                  ×
                                </button>
                              )}
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                <span style={{ fontSize: 11, color: '#DF83A9' }}>ejer:</span>
                                <input
                                  type="number"
                                  min="0"
                                  value={(editingValues[`${item.categoryId}-${item.name}-totalOwned`] ?? item.totalOwned) || ''}
                                  onChange={e => {
                                    setEditingValues({ ...editingValues, [`${item.categoryId}-${item.name}-totalOwned`]: parseInt(e.target.value) || 0 })
                                    handleUpdateItem(item, 'totalOwned', e.target.value)
                                  }}
                                  style={{
                                    width: 45,
                                    padding: '4px 6px',
                                    border: '1px solid #e0e0e0',
                                    borderRadius: 6,
                                    fontSize: 11,
                                  }}
                                  placeholder="0"
                                />
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                <span style={{ fontSize: 11, color: '#DF83A9' }}>skal bruge:</span>
                                <input
                                  type="number"
                                  min="0"
                                  value={(editingValues[`${item.categoryId}-${item.name}-neededForEvent`] ?? item.neededForEvent) || ''}
                                  onChange={e => {
                                    setEditingValues({ ...editingValues, [`${item.categoryId}-${item.name}-neededForEvent`]: parseInt(e.target.value) || 0 })
                                    handleUpdateItem(item, 'neededForEvent', e.target.value)
                                  }}
                                  style={{
                                    width: 45,
                                    padding: '4px 6px',
                                    border: '1px solid #e0e0e0',
                                    borderRadius: 6,
                                    fontSize: 11,
                                  }}
                                  placeholder="0"
                                />
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                <span style={{ fontSize: 11, color: '#DF83A9' }}>låne:</span>
                                <input
                                  type="number"
                                  min="0"
                                  value={Object.values(loans).filter(l => l.item === item.name).reduce((sum, l) => sum + l.amount, 0)}
                                  onChange={e => setLoanForm({ item: item.name, person: '', amount: parseInt(e.target.value) || 0 })}
                                  style={{
                                    width: 45,
                                    padding: '4px 6px',
                                    border: '1px solid #e0e0e0',
                                    borderRadius: 6,
                                    fontSize: 11,
                                    cursor: 'pointer',
                                  }}
                                  placeholder="0"
                                />
                              </div>
                            </div>
                          </div>
                      ))}

                      {addForm?.categoryId === categoryId ? (
                        <div style={{ display: 'flex', gap: 8, padding: '10px 14px', alignItems: 'center' }}>
                          <input
                            className="input"
                            placeholder="navn på vare..."
                            value={addForm.name}
                            onChange={e => setAddForm({ ...addForm, name: e.target.value })}
                            style={{ flex: 1, height: 32 }}
                          />
                          <button
                            className="btn-primary"
                            onClick={() => {
                              handleAddItem(addForm.categoryId, addForm.name)
                            }}
                            style={{ flexShrink: 0 }}
                          >
                            tilføj
                          </button>
                          <button
                            onClick={() => setAddForm(null)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#DF83A9' }}
                          >
                            ×
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setAddForm({ categoryId, name: '' })}
                          style={{
                            fontSize: 12,
                            fontWeight: 600,
                            color: '#6c63ff',
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '8px 14px',
                            fontFamily: 'inherit',
                          }}
                        >
                          + tilføj vare
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )
            })}
          </div>

          <button
            onClick={() => setAddCategoryModal(true)}
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: '#6c63ff',
              background: '#f7f5ff',
              border: '1px solid #e0d5ff',
              borderRadius: 8,
              padding: '10px 14px',
              cursor: 'pointer',
              marginTop: 16,
              fontFamily: 'inherit',
            }}
          >
            + tilføj kategori
          </button>
        </div>
      </div>

      {loanForm && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setLoanForm(null)}>
          <div className="modal" style={{ maxWidth: 400 }}>
            <div className="modal-header">
              <div><div className="modal-title">Låner {loanForm.item}</div></div>
              <button className="modal-close" onClick={() => setLoanForm(null)}>×</button>
            </div>
            <div className="modal-body">
              <div className="input-group">
                <label className="input-label">Hvem låner du af?</label>
                <input
                  className="input"
                  placeholder="fx Anders, Mette..."
                  value={loanForm.person}
                  onChange={e => setLoanForm({ ...loanForm, person: e.target.value })}
                />
              </div>
              <div className="input-group">
                <label className="input-label">Antal</label>
                <input
                  className="input"
                  type="number"
                  min="1"
                  value={loanForm.amount}
                  onChange={e => setLoanForm({ ...loanForm, amount: parseInt(e.target.value) || 1 })}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="modal-cancel" onClick={() => setLoanForm(null)}>annuller</button>
              <button
                className="modal-confirm"
                onClick={() => {
                  addLoan(loanForm.item, loanForm.person, loanForm.amount)
                }}
              >
                tilføj låne
              </button>
            </div>
          </div>
        </div>
      )}

      {showStatModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowStatModal(null)}>
          <div className="modal" style={{ maxWidth: 500 }}>
            <div className="modal-header">
              <div>
                <div className="modal-title">
                  {showStatModal === 'harNok' && '✓ Har nok'}
                  {showStatModal === 'mangler' && 'âš ï¸ Mangler'}
                  {showStatModal === 'skalLåne' && 'ðŸ”„ Skal låne'}
                </div>
              </div>
              <button className="modal-close" onClick={() => setShowStatModal(null)}>×</button>
            </div>
            <div className="modal-body">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {(showStatModal === 'harNok' ? stats.harNok : showStatModal === 'mangler' ? stats.mangler : stats.skalLåne).map(item => (
                  <div key={item.id} style={{ background: '#f7f5ff', borderRadius: 8, padding: 12 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, alignItems: 'center' }}>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#1a1a2e' }}>{item.name}</div>
                        <div style={{ fontSize: 10, color: '#DF83A9', marginTop: 2 }}>{item.categoryId}</div>
                      </div>
                      {showStatModal === 'harNok' && (
                        <span style={{ fontSize: 13, fontWeight: 600, color: '#007a58' }}>{item.neededForEvent}</span>
                      )}
                      {showStatModal === 'mangler' && (
                        <span style={{ fontSize: 13, fontWeight: 600, color: '#9a5800' }}>{item.neededForEvent - item.totalOwned}</span>
                      )}
                      {showStatModal === 'skalLåne' && (
                        <span style={{ fontSize: 13, fontWeight: 600, color: '#cc2020' }}>{item.neededForEvent}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="modal-footer">
              <button className="modal-confirm" onClick={() => setShowStatModal(null)}>luk</button>
            </div>
          </div>
        </div>
      )}

      {addCategoryModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setAddCategoryModal(false)}>
          <div className="modal" style={{ maxWidth: 400 }}>
            <div className="modal-header">
              <div><div className="modal-title">tilføj kategori</div></div>
              <button className="modal-close" onClick={() => setAddCategoryModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="input-group">
                <label className="input-label">kategori navn</label>
                <input
                  className="input"
                  placeholder="fx Dekoration"
                  value={newCategoryName}
                  onChange={e => setNewCategoryName(e.target.value)}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="modal-cancel" onClick={() => setAddCategoryModal(false)}>annuller</button>
              <button
                className="modal-confirm"
                onClick={() => {
                  handleAddCategory()
                  // After adding a category, user can add items to it
                }}
              >
                tilføj kategori
              </button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  )
}


